import { useEffect, useRef, useState } from 'react';
import { FiPlus, FiShoppingCart } from 'react-icons/fi';
import { IoClose, IoSearch } from 'react-icons/io5';
import { MdGrid3X3, MdInventory } from 'react-icons/md';

import { useApp } from '@/context/app';
import { useCart } from '@/context/cart';
import { useSocketIO } from '@/context/socket';
import { useCartMode } from '@/hooks/useCartMode';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Input, Label } from '@hubmaster/ui';
import { Button } from '@hubmaster/ui/Button';
import { ReloadIcon } from '@radix-ui/react-icons';
import { toast } from 'sonner';

import { Image } from './Image';
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from './ui/drawer';

interface ProductItem {
	id?: number;
	barcode?: string;
	name: string;
	price?: number;
	coast?: number;
	stock?: number;
	unit?: string;
}

interface SearchModalProps {
	onSelect: (product: ProductItem) => void;
	onBarcode: (value: string) => void;
	disabled?: boolean;
}

const fmt = (v?: number) =>
	v != null
		? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
		: '—';

export function SearchModal({
	onSelect,
	onBarcode,
	disabled,
}: SearchModalProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState<ProductItem[]>([]);
	const [related, setRelated] = useState<ProductItem[]>([]);
	const { socket, statusSocket } = useSocketIO();
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const mode = useCartMode();
	const { addItem } = useCart();
	const { connection } = useApp();

	const [resultsRef] = useAutoAnimate<HTMLUListElement>();
	const [relatedRef] = useAutoAnimate<HTMLDivElement>();

	const canCart =
		mode.isPurchaseOrder || Boolean(connection.functions?.includes('cart'));

	function handleClose() {
		setOpen(false);
		setSearch('');
		setResults([]);
		setRelated([]);
	}

	function handleSelect(product: ProductItem) {
		onSelect(product);
		handleClose();
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key !== 'Enter') return;
		const term = search.trim();
		if (!term || !/^\d+$/.test(term)) {
			inputRef.current?.blur();
			return;
		}
		onBarcode(term);
		handleClose();
	}

	function handleAddToCart(e: React.MouseEvent, product: ProductItem) {
		e.stopPropagation();
		if (!product.id || !product.price) return;

		const isWeight =
			product.unit?.toUpperCase() === 'KG' ||
			product.unit?.toUpperCase() === 'G';
		const qty = isWeight ? 0.1 : 1;

		addItem(
			{
				id: product.id,
				barcode: product.barcode ?? '',
				name: product.name,
				price: product.price,
				coast: product.coast,
				stock: product.stock,
				unit: product.unit,
			},
			qty,
		);
		toast.success(mode.toastAdded, {
			description: `${qty} ${product.unit ?? 'un'} de ${product.name}`,
		});
	}

	useEffect(() => {
		if (!open) return;

		const term = search.trim();
		if (!term || term.length < 2) {
			setResults([]);
			setRelated([]);
			return;
		}

		// Só números → aguarda Enter, não faz busca por nome
		if (/^\d+$/.test(term)) {
			setResults([]);
			setRelated([]);
			return;
		}

		if (debounceRef.current) clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(() => {
			if (statusSocket !== 'connected') return;

			setLoading(true);
			socket.off('resultConsultByName');

			socket.emit('consult', { consult: 'productByName', data: term });

			socket.once(
				'resultConsultByName',
				(response: { results: ProductItem[]; related: ProductItem[] }) => {
					setResults(response.results ?? []);
					setRelated(response.related ?? []);
					setLoading(false);
				},
			);
		}, 400);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [search, open]);

	// cleanup ao fechar
	useEffect(() => {
		if (!open) {
			socket.off('resultConsultByName');
			setLoading(false);
		}
	}, [open]);

	return (
		<Drawer
			open={open}
			onOpenChange={setOpen}
			onAnimationEnd={() => inputRef.current?.focus()}
		>
			<DrawerTrigger asChild>
				<Button
					size="icon"
					className="h-12 w-12 rounded-xl"
					disabled={disabled}
					title="Buscar por nome"
					icon={<IoSearch size={20} />}
				/>
			</DrawerTrigger>
			<DrawerContent className="sm:w-[400px] grid max-h-[85lvh] min-h-[50dvh] w-full grid-rows-[auto_auto_1fr_auto] px-4 pb-4">
				<DrawerHeader className="pb-2">
					<DrawerTitle>Buscar produto</DrawerTitle>
				</DrawerHeader>
				<div className="no-scrollbar mt-5 overflow-y-auto">
					{/* Estado vazio */}
					{!loading &&
						search.trim().length >= 2 &&
						results.length === 0 &&
						related.length === 0 && (
							<p className="mt-4 text-center text-sm text-gray-400">
								Nenhum produto encontrado para <strong>"{search}"</strong>
							</p>
						)}
					{!search.trim() && (
						<p className="mt-4 text-center text-sm text-gray-400">
							Digite ao menos 2 caracteres para buscar
						</p>
					)}

					{/* Resultados */}
					{results.length > 0 && (
						<p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-400">
							Resultados
						</p>
					)}
					<ul ref={resultsRef} className="flex flex-col gap-1">
						{results.length > 0 &&
							results.map((p, i) => (
								<ProductRow
									key={p.id ?? i}
									product={p}
									onSelect={handleSelect}
									onAddToCart={handleAddToCart}
									showCartButton={canCart && !!p.id && !!p.price}
									cartLabel={mode.addButton}
								/>
							))}
					</ul>
					{/* Correlatos */}
					{related.length > 0 && (
						<p className="mb-2 mt-5 text-sm font-semibold uppercase tracking-wide text-gray-400">
							Relacionados
						</p>
					)}
					<div ref={relatedRef} className="grid grid-cols-2 gap-2">
						{related.length > 0 &&
							related.map((p, i) => (
								<RelatedChip
									key={p.id ?? i}
									product={p}
									onSelect={handleSelect}
									onAddToCart={handleAddToCart}
									showCartButton={canCart && !!p.id && !!p.price}
								/>
							))}
					</div>
				</div>
				{/* Input de busca */}
				<div>
					<div className="relative mt-4">
						<Label
							htmlFor="search"
							className="absolute left-3 top-1/2 -translate-y-1/2 text-primary"
						>
							<IoSearch size={16} />
						</Label>

						<Input
							ref={inputRef}
							type="text"
							id="search"
							placeholder="Nome, código ou código de barras..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onKeyDown={handleKeyDown}
							className="h-12 rounded-2xl border-2 border-input pl-9"
						/>
						{loading && (
							<ReloadIcon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-primary" />
						)}
						{search && !loading && (
							<Button
								variant="ghost"
								size="icon"
								className="absolute right-3 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full text-primary"
								onClick={() => setSearch('')}
							>
								<IoClose size={16} />
							</Button>
						)}
					</div>
					{/^\d+$/.test(search.trim()) && search.trim().length >= 1 && (
						<p className="mt-1.5 px-1 text-xs text-gray-400">
							Pressione{' '}
							<kbd className="rounded border bg-gray-100 px-1 py-0.5 font-mono text-xs">
								Enter
							</kbd>{' '}
							para buscar por{' '}
							<strong>
								{search.trim().length < 7 ? 'código' : 'código de barras'}
							</strong>
						</p>
					)}
				</div>
			</DrawerContent>
		</Drawer>
	);
}

function ProductRow({
	product,
	onSelect,
	onAddToCart,
	showCartButton,
	cartLabel,
}: {
	product: ProductItem;
	onSelect: (p: ProductItem) => void;
	onAddToCart: (e: React.MouseEvent, p: ProductItem) => void;
	showCartButton: boolean;
	cartLabel: string;
}) {
	return (
		<li className="flex items-center gap-2 rounded-xl border bg-white transition-colors hover:bg-gray-50">
			{/* Imagem do produto */}
			{product.barcode && (
				<Image
					src={`https://cdn-cosmos.bluesoft.com.br/products/${product.barcode}`}
					alt=""
					className="h-14 w-14 shrink-0 rounded-l-xl rounded-r-none border-r"
					typeLoading="bar"
				/>
			)}
			{!product.barcode && (
				<div className="h-14 w-14 shrink-0 rounded-l-xl bg-gray-100" />
			)}

			{/* Info — clicável para selecionar */}
			<button
				onClick={() => onSelect(product)}
				className="flex flex-1 flex-col py-2 text-left"
			>
				<p className="line-clamp-2 text-sm font-semibold leading-tight text-foreground">
					{product.name}
				</p>
				{product.id && (
					<p className="mt-0.5 flex items-center gap-1 text-xs text-gray-400">
						<MdGrid3X3 size={11} />
						{product.id}
						{product.barcode && (
							<span className="ml-1">&bull; {product.barcode}</span>
						)}{' '}
						{product.stock && (
							<span className="ml-1 flex items-center gap-0.5">
								&bull;
								<MdInventory /> {product.stock}
							</span>
						)}
					</p>
				)}
			</button>

			{/* Preço + botão adicionar */}
			<div className="flex shrink-0 flex-col items-end gap-1 py-2 pr-3">
				<p className="text-base font-bold leading-none text-primary">
					{fmt(product.price)}
				</p>
				{product.unit && product.unit !== 'UN' && (
					<p className="text-xs leading-none text-gray-400">
						/{product.unit.toLowerCase()}
					</p>
				)}
				{showCartButton && (
					<Button onClick={(e) => onAddToCart(e, product)} title={cartLabel}>
						<FiPlus size={12} />
						<FiShoppingCart size={12} />
					</Button>
				)}
			</div>
		</li>
	);
}

function RelatedChip({
	product,
	onSelect,
	onAddToCart,
	showCartButton,
}: {
	product: ProductItem;
	onSelect: (p: ProductItem) => void;
	onAddToCart: (e: React.MouseEvent, p: ProductItem) => void;
	showCartButton: boolean;
}) {
	return (
		<div className="hover:bg-primary/10 flex w-full flex-col items-start rounded-xl border bg-gray-50 transition-colors">
			{/* Imagem */}
			{product.barcode && (
				<Image
					src={`https://cdn-cosmos.bluesoft.com.br/products/${product.barcode}`}
					alt=""
					className="h-20 w-full rounded-b-none rounded-t-xl border-b"
					typeLoading="bar"
				/>
			)}

			{/* Info + ações */}
			<div className="flex w-full flex-col px-2 pb-2 pt-1">
				<button onClick={() => onSelect(product)} className="text-left">
					<span className="line-clamp-2 max-w-[8rem] text-xs font-medium leading-tight text-foreground">
						{product.name}
					</span>
					<span className="mb-1 mt-0.5 block text-xs font-semibold text-primary">
						{fmt(product.price)}
					</span>
				</button>

				{showCartButton && (
					<Button onClick={(e) => onAddToCart(e, product)}>
						<FiPlus size={11} />
						<FiShoppingCart size={11} />
					</Button>
				)}
			</div>
		</div>
	);
}
